import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { cloneDeep } from 'src/ep-utils';
import { EditStatusType, StateHelperService } from '../../../services/proprietary/state-helper.service';
import { ContractObjectDefaults } from './contract-object.defaults';
import { AddPriorHistoryPointer, RemovePriorHistoryPointer } from './contract.actions';
import {
    AddLossHistory,
    AddPriorHistory,
    DeleteLossHistory,
    DeletePriorHistory,
    RecalcLossHistory,
    RecalcPriorHistory,
    RestoreLossHistory,
    RestorePriorHistory,
    UpdateLossHistory,
    UpdatePriorHistory
} from './prior-history.actions';

export interface PriorHistoryStateModel {
    priorHistory: any;
    lossHistory: any;
}

@State<PriorHistoryStateModel>({
    name: 'priorHistoryState',
    defaults: {
        priorHistory: {},
        lossHistory: {}
    }
})
@Injectable()
export class PriorHistoryState {
    constructor(private store: Store, private helper: StateHelperService) { }

    //******* SELECTORS *********
    @Selector()
    static priorHistories(state: PriorHistoryStateModel) {
        //return all prior histories
        let result: any = [];
        for (var i in state.priorHistory) {
            result.push(state.priorHistory[i]);
        }
        return result;
    }

    @Selector()
    static priorHistStatusByID(state: PriorHistoryStateModel) {
        return (priorHistID: any) => {
            return state.priorHistory[priorHistID]?.Edit_Status || 'N';
        };
    }

    @Selector()
    static lossHistoriesByPriorHistoryID(state: PriorHistoryStateModel) {
        //return all lossHistories for a particular Prior History based on Prior History ID
        return (priorHistID: any) => {
            const priorHistory = state.priorHistory[priorHistID];
            let lossHistories: any[] = [];
            //Don't include records if parent is in a deleted status.
            if (priorHistory && priorHistory.Edit_Status != EditStatusType.Delete) {
                for (var i in state.lossHistory) {
                    if (state.lossHistory[i].PriorHist_ID == priorHistID) {
                        lossHistories.push(state.lossHistory[i]);
                    }
                }
            }
            return lossHistories;
        };
    }

    //******  ACTIONS  ******

    @Action(UpdatePriorHistory)
    updatePriorHistory(sc: StateContext<PriorHistoryStateModel>, action: UpdatePriorHistory) {
        const state = sc.getState();
        let priorHistoryID = action.priorHistory.PriorHist_ID;
        let updatePriorHistory: any = { ...action.priorHistory };
        const currentPriorHistory = cloneDeep(state.priorHistory[priorHistoryID]);
        this.helper.setItemChanged(updatePriorHistory, currentPriorHistory.Edit_Status);

        let resetBuckets = false;
        let removeChildRecords = false;
        let recalc = false;

        if (this.helper.stateValueChanged(updatePriorHistory.Detail_Loss_Ind, currentPriorHistory.Detail_Loss_Ind)) {
            resetBuckets = true;

            if (updatePriorHistory.Detail_Loss_Ind == 'N') {
                removeChildRecords = true;
            }

            updatePriorHistory.Open_Claim_Count = '0';
            updatePriorHistory.Closed_Claim_Count = '0';
            updatePriorHistory.Loss_Valuation_Date = '';
            updatePriorHistory.Net_Loss_Ratio = '0';
            updatePriorHistory.Dev_Loss_Ratio = '0';
            updatePriorHistory.Tot_Avail_Reserve = '0.00';
            updatePriorHistory.Tot_Avail_Exp_Reserve = '0.00';
            updatePriorHistory.Tot_Pay_Made = '0.00';
            updatePriorHistory.Tot_Exp_Pay_Made = '0.00';
            updatePriorHistory.Tot_Incured = '0.00';
            updatePriorHistory.Tot_Recovery = '0.00';
            updatePriorHistory.Net_of_Recoveries = '0.00';
        }

        if (!resetBuckets) {
            if (this.helper.stateValueChanged(updatePriorHistory.Tot_Avail_Reserve, currentPriorHistory.Tot_Avail_Reserve)) {
                recalc = true;
            }
            if (
                this.helper.stateValueChanged(
                    updatePriorHistory.Tot_Avail_Exp_Reserve,
                    currentPriorHistory.Tot_Avail_Exp_Reserve
                )
            ) {
                recalc = true;
            }
            if (this.helper.stateValueChanged(updatePriorHistory.Tot_Pay_Made, currentPriorHistory.Tot_Pay_Made)) {
                recalc = true;
            }
            if (this.helper.stateValueChanged(updatePriorHistory.Tot_Exp_Pay_Made, currentPriorHistory.Tot_Exp_Pay_Made)) {
                recalc = true;
            }
            if (this.helper.stateValueChanged(updatePriorHistory.Tot_Recovery, currentPriorHistory.Tot_Recovery)) {
                recalc = true;
            }
            if (this.helper.stateValueChanged(updatePriorHistory.Prior_Premium, currentPriorHistory.Prior_Premium)) {
                recalc = true;
            }
            if (this.helper.stateValueChanged(updatePriorHistory.Prior_Year_Seq, currentPriorHistory.Prior_Year_Seq)) {
                recalc = true;
            }
        }
        sc.patchState({
            priorHistory: {
                ...state.priorHistory,
                [priorHistoryID]: {
                    ...currentPriorHistory,
                    ...updatePriorHistory
                }
            }
        });

        if (removeChildRecords) {
            let lossHistories: any[] = [];
            for (var i in state.lossHistory) {
                if (
                    state.lossHistory[i].PriorHist_ID == priorHistoryID &&
                    state.lossHistory[i].Edit_Status != EditStatusType.Delete
                ) {
                    lossHistories.push(state.lossHistory[i]);
                }
            }

            for (var loss in lossHistories) {
                sc.dispatch(new DeleteLossHistory(lossHistories[loss].LossHist_ID));
            }
        }

        if (recalc) {
            sc.dispatch(new RecalcPriorHistory(priorHistoryID));
        }
    }

    @Action(AddPriorHistory)
    addPriorHistory(sc: StateContext<PriorHistoryStateModel>, action: AddPriorHistory) {
        const state = sc.getState();
        let newID = this.helper.getTempID();
        let newPriorHistory = this.helper.createDefaultItem(ContractObjectDefaults, 'Prior_History', action.priorHistory);
        this.helper.setItemAdded(newPriorHistory);
        newPriorHistory.PriorHist_ID = newID;

        sc.patchState({
            priorHistory: { ...state.priorHistory, [newID]: newPriorHistory }
        });

        sc.dispatch(new AddPriorHistoryPointer(newID)); // add prior history to list array on contract node (for denormalization)
    }

    @Action(DeletePriorHistory)
    deletePriorHistory(sc: StateContext<PriorHistoryStateModel>, action: DeletePriorHistory) {
        const state = sc.getState();
        let priorHistoryID = parseInt(action.priorHistoryID, 10);
        let priorHistory = { ...state.priorHistory };
        this.helper.setItemDeleted(priorHistory, priorHistoryID);
        sc.patchState({ priorHistory: priorHistory });

        if (priorHistoryID < 0) {
            sc.dispatch(new RemovePriorHistoryPointer(action.priorHistoryID));
        }

        let lossHistories: any[] = [];
        for (var i in state.lossHistory) {
            if (
                state.lossHistory[i].PriorHist_ID == priorHistoryID &&
                state.lossHistory[i].Edit_Status != EditStatusType.Delete
            ) {
                lossHistories.push(state.lossHistory[i]);
            }
        }

        for (var loss in lossHistories) {
            sc.dispatch(new DeleteLossHistory(lossHistories[loss].LossHist_ID));
        }
    }

    @Action(RestorePriorHistory)
    restorePriorHistory(sc: StateContext<PriorHistoryStateModel>, action: RestorePriorHistory) {
        const state = sc.getState();
        const priorHistoryID = action.priorHistoryID;
        let priorHistory = { ...state.priorHistory };
        this.helper.setItemRestored(priorHistory, priorHistoryID);
        sc.patchState({ priorHistory: priorHistory });

        if (priorHistory[priorHistoryID]?.Detail_Loss_Ind == 'Y') {
            let lossHistories: any[] = [];
            for (var i in state.lossHistory) {
                if (
                    state.lossHistory[i].PriorHist_ID == priorHistoryID &&
                    state.lossHistory[i].Edit_Status == EditStatusType.Delete
                ) {
                    lossHistories.push(state.lossHistory[i]);
                }
            }

            for (var loss in lossHistories) {
                sc.dispatch(new RestoreLossHistory(lossHistories[loss].LossHist_ID));
            }
        }
    }

    @Action(UpdateLossHistory)
    updateLossHistory(sc: StateContext<PriorHistoryStateModel>, action: UpdateLossHistory) {
        const state = sc.getState();
        let updateLossHistory: any = { ...action.lossHistory };
        const lossHistoryID = updateLossHistory.LossHist_ID;
        const currentLossHistory = cloneDeep(state.lossHistory[lossHistoryID]);
        const priorHistoryID = currentLossHistory.PriorHist_ID;

        this.helper.setItemChanged(updateLossHistory, currentLossHistory.Edit_Status);

        let recalc = false;

        if (this.helper.stateValueChanged(updateLossHistory.Claim_Status, currentLossHistory.Claim_Status)) {
            recalc = true;
        }
        if (this.helper.stateValueChanged(updateLossHistory.Avail_Reserve, currentLossHistory.Avail_Reserve)) {
            recalc = true;
        }
        if (this.helper.stateValueChanged(updateLossHistory.Avail_Exp_Reserve, currentLossHistory.Avail_Exp_Reserve)) {
            recalc = true;
        }
        if (this.helper.stateValueChanged(updateLossHistory.Pay_Made, currentLossHistory.Pay_Made)) {
            recalc = true;
        }
        if (this.helper.stateValueChanged(updateLossHistory.Exp_Pay_Made, currentLossHistory.Exp_Pay_Made)) {
            recalc = true;
        }
        if (this.helper.stateValueChanged(updateLossHistory.Recovery, currentLossHistory.Recovery)) {
            recalc = true;
        }

        //Mark Parent As Changed.
        let priorHistory = cloneDeep(state.priorHistory[priorHistoryID]);
        this.helper.setItemChanged(priorHistory, priorHistory.Edit_Status);

        sc.patchState({
            lossHistory: {
                ...state.lossHistory,
                [lossHistoryID]: {
                    ...currentLossHistory,
                    ...updateLossHistory
                }
            },
            priorHistory: {
                ...state.priorHistory,
                [priorHistoryID]: priorHistory
            }
        });

        if (recalc) {
            sc.dispatch(new RecalcLossHistory(lossHistoryID));
        }
    }

    @Action(DeleteLossHistory)
    deleteLossHistory(sc: StateContext<PriorHistoryStateModel>, action: DeleteLossHistory) {
        const state = sc.getState();
        const lossHistID = action.lossHistoryID;
        const priorHistoryID = state.lossHistory[lossHistID].PriorHist_ID;
        let priorHistory: any = cloneDeep(state.priorHistory[priorHistoryID]);
        let lossHistory = { ...state.lossHistory };

        this.helper.setItemDeleted(lossHistory, lossHistID);
        sc.patchState({
            lossHistory: lossHistory
        });

        // remove pointer from parent priorHistory record
        if (priorHistory) {
            if (parseInt(lossHistID, 10) < 0) {
                priorHistory.Loss_Summary_Detail = this.helper.removeArrayItemByValue(
                    priorHistory.Loss_Summary_Detail,
                    action.lossHistoryID
                );
            }

            // mark parent as changed.
            this.helper.setItemChanged(priorHistory, priorHistory.Edit_Status);

            sc.patchState({
                priorHistory: {
                    ...state.priorHistory,
                    [priorHistoryID]: priorHistory
                }
            });

            if (priorHistory.Detail_Loss_Ind == 'Y') {
                sc.dispatch(new RecalcPriorHistory(priorHistoryID));
            }
        }
    }

    @Action(AddLossHistory)
    addLossHistory(sc: StateContext<PriorHistoryStateModel>, action: AddLossHistory) {
        const state = sc.getState();
        const priorHistoryID: string = action.priorHistoryID;
        let priorHistory = cloneDeep(state.priorHistory[priorHistoryID]);
        let newLossHistoryID: string = this.helper.getTempID();
        let newLossHistory = this.helper.createDefaultItem(
            ContractObjectDefaults,
            'Loss_Summary_Detail',
            action.lossHistory
        );
        newLossHistory.PriorHist_ID = priorHistoryID;
        this.helper.setItemAdded(newLossHistory);
        newLossHistory.LossHist_ID = newLossHistoryID;

        //Mark Parent As Changed.
        this.helper.setItemChanged(priorHistory, priorHistory.Edit_Status);

        //Add Pointer
        if (!priorHistory.Loss_Summary_Detail) {
            priorHistory.Loss_Summary_Detail = [];
        }

        priorHistory.Loss_Summary_Detail = [...priorHistory.Loss_Summary_Detail, newLossHistoryID];

        sc.patchState({
            lossHistory: { ...state.lossHistory, [newLossHistoryID]: newLossHistory },
            priorHistory: {
                ...state.priorHistory,
                [priorHistoryID]: priorHistory
            }
        });
    }

    @Action(RestoreLossHistory)
    restoreLossHistory(sc: StateContext<PriorHistoryStateModel>, action: RestoreLossHistory) {
        const state = sc.getState();
        const lossHistoryID = parseInt(action.lossHistoryID, 10);
        let lossHistory = { ...state.lossHistory };
        const priorHistoryID = lossHistory[lossHistoryID].PriorHist_ID;

        this.helper.setItemRestored(lossHistory, lossHistoryID);
        sc.patchState({ lossHistory: lossHistory });

        sc.dispatch(new RecalcPriorHistory(priorHistoryID));
    }

    @Action(RecalcLossHistory)
    recalcLossHistory(sc: StateContext<PriorHistoryStateModel>, action: RecalcLossHistory) {
        const state = sc.getState();
        let lossHistory: any = cloneDeep(state.lossHistory[action.lossHistoryID]);
        const priorHistoryID = lossHistory.PriorHist_ID;

        if (!lossHistory.Avail_Reserve) {
            lossHistory.Avail_Reserve = 0;
        }
        if (!lossHistory.Avail_Exp_Reserve) {
            lossHistory.Avail_Exp_Reserve = 0;
        }
        if (!lossHistory.Pay_Made) {
            lossHistory.Pay_Made = 0;
        }
        if (!lossHistory.Exp_Pay_Made) {
            lossHistory.Exp_Pay_Made = 0;
        }
        if (!lossHistory.Recovery) {
            lossHistory.Recovery = 0;
        }

        // Calculate total incurred
        lossHistory.Incurred = (
            Number(lossHistory.Avail_Reserve) +
            Number(lossHistory.Avail_Exp_Reserve) +
            Number(lossHistory.Pay_Made) +
            Number(lossHistory.Exp_Pay_Made)
        ).toString();

        // Calculate net incurred
        lossHistory.Net_of_Recoveries = (Number(lossHistory.Incurred) - Number(lossHistory.Recovery)).toString();

        sc.patchState({
            lossHistory: {
                ...state.lossHistory,
                [action.lossHistoryID]: lossHistory
            }
        });

        sc.dispatch(new RecalcPriorHistory(priorHistoryID));
    }

    @Action(RecalcPriorHistory)
    recalcPriorHistory(sc: StateContext<PriorHistoryStateModel>, action: RecalcPriorHistory) {
        const state = sc.getState();
        const priorHistoryID = action.priorHistoryID;
        let priorHistory: any = cloneDeep(state.priorHistory[priorHistoryID]);

        if (!priorHistory.Prior_Premium) {
            priorHistory.Prior_Premium = 0;
        }
        if (!priorHistory.Tot_Avail_Reserve) {
            priorHistory.Tot_Avail_Reserve = 0;
        }
        if (!priorHistory.Tot_Avail_Exp_Reserve) {
            priorHistory.Tot_Avail_Exp_Reserve = 0;
        }
        if (!priorHistory.Tot_Pay_Made) {
            priorHistory.Tot_Pay_Made = 0;
        }
        if (!priorHistory.Tot_Exp_Pay_Made) {
            priorHistory.Tot_Exp_Pay_Made = 0;
        }
        if (!priorHistory.Tot_Recovery) {
            priorHistory.Tot_Recovery = 0;
        }

        // Get loss dev year
        let devfactor = 0;
        let priorYearSeq = priorHistory.Prior_Year_Seq;
        if (!priorYearSeq || priorYearSeq <= 0) {
            priorYearSeq = 1;
        }

        // Set factor
        switch (parseInt(priorYearSeq, 10)) {
            case 1:
                devfactor = 1.3;
                break;
            case 2:
                devfactor = 1.2;
                break;
            case 3:
                devfactor = 1.125;
                break;
            case 4:
                devfactor = 1.075;
                break;
            case 5:
                devfactor = 1.05;
                break;
            default:
                devfactor = 1.0;
                break;
        }

        if (priorHistory.Detail_Loss_Ind == 'Y') {
            let lossHistories: any[] = [];
            for (var i in state.lossHistory) {
                if (
                    state.lossHistory[i].PriorHist_ID == priorHistoryID &&
                    state.lossHistory[i].Edit_Status != EditStatusType.Delete
                ) {
                    lossHistories.push(state.lossHistory[i]);
                }
            }

            // Initialize / Clear buckets
            priorHistory.Open_Claim_Count = '0';
            priorHistory.Closed_Claim_Count = '0';
            priorHistory.Loss_Valuation_Date = '';
            priorHistory.Net_Loss_Ratio = '0';
            priorHistory.Dev_Loss_Ratio = '0';
            priorHistory.Tot_Avail_Reserve = '0.00';
            priorHistory.Tot_Avail_Exp_Reserve = '0.00';
            priorHistory.Tot_Pay_Made = '0.00';
            priorHistory.Tot_Exp_Pay_Made = '0.00';
            priorHistory.Tot_Incured = '0.00';
            priorHistory.Tot_Recovery = '0.00';
            priorHistory.Net_of_Recoveries = '0.00';

            if (lossHistories.length > 0) {
                let openClaims = 0;
                let closedClaims = 0;

                let totAvailReserve = 0;
                let totAvailExpReserve = 0;
                let totPayMade = 0;
                let totPayExpMade = 0;
                let totIncured = 0;
                let totRecovery = 0;
                let netOfRecoveries = 0;

                for (var loss in lossHistories) {
                    const lossHistory = lossHistories[loss];

                    switch (lossHistory.Claim_Status) {
                        case 'O':
                            openClaims++;
                            break;

                        case 'C':
                            closedClaims++;
                            break;
                    }

                    totAvailReserve += Number(lossHistory.Avail_Reserve || 0);
                    totAvailExpReserve += Number(lossHistory.Avail_Exp_Reserve || 0);
                    totPayMade += Number(lossHistory.Pay_Made || 0);
                    totPayExpMade += Number(lossHistory.Exp_Pay_Made || 0);
                    totIncured += Number(lossHistory.Incurred || 0);
                    totRecovery += Number(lossHistory.Recovery || 0);
                    netOfRecoveries += Number(lossHistory.Net_of_Recoveries || 0);
                }

                priorHistory.Open_Claim_Count = openClaims.toString();
                priorHistory.Closed_Claim_Count = closedClaims.toString();
                priorHistory.Tot_Avail_Reserve = totAvailReserve.toFixed(2);
                priorHistory.Tot_Avail_Exp_Reserve = totAvailExpReserve.toFixed(2);
                priorHistory.Tot_Pay_Made = totPayMade.toFixed(2);
                priorHistory.Tot_Exp_Pay_Made = totPayExpMade.toFixed(2);
                priorHistory.Tot_Incured = totIncured.toFixed(2);
                priorHistory.Tot_Recovery = totRecovery.toFixed(2);
                priorHistory.Net_of_Recoveries = netOfRecoveries.toFixed(2);

                if (Number(priorHistory.Prior_Premium) != 0) {
                    priorHistory.Net_Loss_Ratio = (
                        ((totIncured * devfactor - totRecovery) / Number(priorHistory.Prior_Premium)) *
                        100
                    ).toFixed(2);

                    priorHistory.Dev_Loss_Ratio = (((totIncured * devfactor) / Number(priorHistory.Prior_Premium)) * 100).toFixed(
                        2
                    );
                } else {
                    priorHistory.Net_Loss_Ratio = '0';
                    priorHistory.Dev_Loss_Ratio = '0';
                }
            }
        } else {
            // Calculate total incurred
            priorHistory.Tot_Incured = (
                Number(priorHistory.Tot_Avail_Reserve) +
                Number(priorHistory.Tot_Avail_Exp_Reserve) +
                Number(priorHistory.Tot_Pay_Made) +
                Number(priorHistory.Tot_Exp_Pay_Made)
            ).toString();

            // Calculate net incurred
            priorHistory.Net_of_Recoveries = (
                Number(priorHistory.Tot_Incured) - Number(priorHistory.Tot_Recovery)
            ).toString();

            // Update ratios
            if (Number(priorHistory.Prior_Premium) != 0) {
                priorHistory.Net_Loss_Ratio = (
                    ((Number(priorHistory.Tot_Incured) * devfactor - Number(priorHistory.Tot_Recovery)) /
                        Number(priorHistory.Prior_Premium)) *
                    100
                ).toFixed(2);

                priorHistory.Dev_Loss_Ratio = (
                    ((Number(priorHistory.Tot_Incured) * devfactor) / Number(priorHistory.Prior_Premium)) *
                    100
                ).toFixed(2);
            } else {
                priorHistory.Net_Loss_Ratio = '0';
                priorHistory.Dev_Loss_Ratio = '0';
            }
        }

        sc.patchState({
            priorHistory: {
                ...state.priorHistory,
                [priorHistoryID]: priorHistory
            }
        });
    }
}
